/**
 * Founders — public "Wall of Fame" page listing every pilot who
 * claimed a Founder's Edition slot. No auth required; designed to be
 * shareable and drive scarcity FOMO.
 *
 * Layout:
 *   [Hero] "Founder's Wall of Fame" · N/100 slots claimed · N remaining
 *   [CTA]  Buy full game to claim your slot (hidden when 0 remaining)
 *   [Grid] Tile per founder: gold #N chip + callsign + claim date
 *   [Empty state] "No founders yet — be the first"
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Crown } from "lucide-react";
import Navbar from "../components/Navbar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Founders() {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Public endpoint — call directly (no cookie needed, no api wrapper).
    axios.get(`${BACKEND}/api/entitlements/founders-wall`)
      .then((r) => setData(r.data))
      .catch(() => setData({ cap: 100, claimed: 0, remaining: 100, founders: [] }));
  }, []);

  const percent = data ? Math.round((data.claimed / data.cap) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#05050a] text-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="founders-wall-page">
        {/* Hero */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ffd700] mb-2">
            // FOUNDERS · WALL OF FAME
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-2">
            The First <span className="text-[#ffd700]">{data?.cap ?? 100}</span>
          </h1>
          <div className="text-white/70 text-sm max-w-2xl">
            The pilots who claimed a Founder&apos;s Edition slot the moment Cosmic Worm Breaker went live.
            Their names live here forever. Each carries a unique ball skin only they can equip.
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8" data-testid="founders-progress">
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-heading text-2xl tabular-nums">
              <span className="text-[#ffd700]">{data?.claimed ?? "…"}</span>
              <span className="text-white/30 text-lg">/{data?.cap ?? 100}</span>
              <span className="ml-3 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/50">
                claimed
              </span>
            </div>
            {data && data.remaining > 0 && (
              <Link
                to="/unlock"
                className="px-4 py-2 border border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10 font-heading uppercase tracking-tight text-sm transition"
                data-testid="founders-cta-buy"
              >
                Claim #{data.claimed + 1}
              </Link>
            )}
          </div>
          <div className="h-2 bg-white/[0.06] border border-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ffd700] via-[#b026ff] to-[#ffd700]"
              style={{ width: `${percent}%`, transition: "width .6s ease" }}
            />
          </div>
        </div>

        {/* Grid */}
        {!data ? (
          <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/40">
            Loading founders…
          </div>
        ) : data.founders.length === 0 ? (
          <div
            className="border-2 border-dashed border-[#ffd700]/40 bg-[#ffd700]/[0.03] p-10 text-center"
            data-testid="founders-empty"
          >
            <Crown className="w-8 h-8 text-[#ffd700] mx-auto mb-3" />
            <div className="font-heading uppercase tracking-tight text-xl text-[#ffd700] mb-2">
              No founders yet
            </div>
            <div className="text-white/60 text-sm max-w-md mx-auto mb-4">
              The wall is empty — the first pilot to unlock will be Founder #1, immortalized here forever.
            </div>
            <Link
              to="/unlock"
              className="inline-block px-5 py-2 bg-[#ffd700] hover:bg-[#ffed7a] text-black font-heading uppercase tracking-tight text-sm"
            >
              Be Founder #1
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
            data-testid="founders-grid"
          >
            {data.founders.map((f) => {
              // Top-3 get a subtle regal treatment; podium spots feel earned.
              const isPodium = f.founder_number <= 3;
              const border = isPodium
                ? "border-[#ffd700]/70 bg-gradient-to-br from-[#ffd700]/[0.10] via-[#b026ff]/[0.06] to-transparent"
                : "border-white/12 bg-white/[0.02] hover:border-[#ffd700]/40";
              return (
                <div
                  key={f.founder_number}
                  className={`border ${border} p-3 transition`}
                  data-testid={`founder-tile-${f.founder_number}`}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-3 h-3 text-[#ffd700]" />
                    <span className="font-heading text-lg text-[#ffd700] tabular-nums">
                      #{f.founder_number}
                    </span>
                  </div>
                  <div className="text-sm text-white truncate" title={f.handle}>
                    {f.name}
                  </div>
                  {f.awarded_at && (
                    <div className="font-mono-cosmic text-[9px] tracking-[0.2em] uppercase text-white/40 mt-1 tabular-nums">
                      {new Date(f.awarded_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
          Refresh live · founders are added the moment they complete their purchase
        </div>
      </main>
    </div>
  );
}
